import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/reducers/rootReducer";
import { getCurrentTheme, Theme } from "../../themes/themeConfig";
import { ACCESS_ALLOWLIST, normalizeLogin } from "../../config/accessAllowlist";
import { config } from "../../config/config";
import "./Sidebar.css";

const menuItems = [
  { id: "executiveOverview", label: "CM Overview", href: "/overview" },
  { id: "program", label: "Program Overview", href: "/programs" },
  { id: "monitorAlert", label: "Monitor & Alert", href: "/monitor" },
  { id: "intelligentAnalysis", label: "Intelligent Analysis", href: "/analysis" },
  { id: "reportAgent", label: "Report Agent", href: "/reports" },
  { id: "processOptimization", label: "Process Optimization", href: "/optimization" },
  { id: "configurationSetting", label: "Configuration Setting", href: "/configuration" },
  { id: "permissionManagement", label: "Permission Management", href: "/permissions" },
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTheme, setCurrentTheme] = useState<Theme>(getCurrentTheme());
  const userName = useSelector((state: RootState) => state.user.userName);

  // Filter menu items based on allowlist access control and environment
  const filteredMenuItems = menuItems.filter((item) => {
    if (item.id === "permissionManagement" && (config.STAGE === "gamma" || config.STAGE === "prod")) {
      return false;
    }
    const allowlist = ACCESS_ALLOWLIST[item.id];
    if (!allowlist) return true; // No allowlist defined, show by default
    return allowlist.includes(normalizeLogin(userName));
  });

  useEffect(() => {
    const updateTheme = () => {
      setCurrentTheme(getCurrentTheme());
      applySidebarStyles();
    };

    // Apply sidebar styles on mount
    applySidebarStyles();

    // Listen for custom theme change events
    window.addEventListener('themeChanged', updateTheme);
    
    return () => {
      window.removeEventListener('themeChanged', updateTheme);
    };
  }, []);

  const applySidebarStyles = () => {
    const theme = getCurrentTheme();
    
    // Don't apply custom sidebar styling for Black/Default theme
    if (theme.name === 'default') {
      // Remove any custom styling to use CloudScape defaults
      setTimeout(() => {
        const sidebarSelectors = [
          '.awsui-side-navigation',
          '[data-testid="side-navigation"]',
          'nav'
        ];
        
        sidebarSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element: any) => {
            if (element.style) {
              element.style.removeProperty('background-color');
              element.style.removeProperty('border-right-color');
              element.style.removeProperty('color');
            }
          });
        });

        const itemSelectors = [
          '.awsui-side-navigation-item',
          '.awsui-side-navigation a',
          '.awsui-side-navigation button'
        ];
        
        itemSelectors.forEach(selector => {
          const elements = document.querySelectorAll(selector);
          elements.forEach((element: any) => {
            if (element.style) {
              element.style.removeProperty('color');
              element.style.removeProperty('background-color');
            }
          });
        });
      }, 100);
      return;
    }
    
    setTimeout(() => {
      // Target sidebar container
      const sidebarSelectors = [
        '.awsui-side-navigation',
        '[data-testid="side-navigation"]',
        'nav'
      ];
      
      sidebarSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element: any) => {
          if (element.style) {
            element.style.setProperty('background-color', theme.colors.navigationBackground, 'important');
            element.style.setProperty('border-right-color', theme.colors.navigationBorder, 'important');
            element.style.setProperty('color', theme.colors.navigationText, 'important');
          }
        });
      });

      // Target sidebar items
      const itemSelectors = [
        '.awsui-side-navigation-item',
        '.awsui-side-navigation a',
        '.awsui-side-navigation button'
      ];
      
      itemSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element: any) => {
          if (element.style) {
            element.style.setProperty('color', theme.colors.navigationText, 'important');
            element.style.setProperty('background-color', 'transparent', 'important');
          }
        });
      });
    }, 100);
  };

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname === href;
  };

  const themeClass =
    currentTheme.name === "default"
      ? "sidebar-theme-default"
      : "sidebar-theme-light";

  return (
    <div
      className={`custom-sidebar ${themeClass}`}
      style={{
        height: '100%',
        ...(currentTheme.name !== 'default' && {
          backgroundColor: currentTheme.colors.navigationBackground,
          borderRight: `1px solid ${currentTheme.colors.navigationBorder}`,
          color: currentTheme.colors.navigationText
        })
      }}
    >
      <div className="sidebar-brand">
        <span className="sidebar-brand-text" onClick={() => navigate("/")}>
          Home
        </span>
      </div>

      <div className="sidebar-divider" />

      {/* <div className="sidebar-section-label">Navigation</div> */}

      <nav className="sidebar-nav">
        {filteredMenuItems.map((item) => (
          <div
            key={item.href}
            className={`sidebar-nav-item ${isActive(item.href) ? "active" : ""}`}
            data-testid={`sidebar-nav-item-${item.href.replace("/", "")}`}
            onClick={() => navigate(item.href)}
          >
            <span className="sidebar-nav-text">{item.label}</span>
          </div>
        ))}
      </nav>
    </div>
  );
};

export default Sidebar;
